<template>
  <div class="data-table">
    <!-- 表格工具栏 -->
    <div class="table-toolbar" v-if="showToolbar">
      <div class="toolbar-left">
        <slot name="toolbar-left"></slot>
      </div>
      <div class="toolbar-right">
        <slot name="toolbar-right">
          <el-input
            v-if="searchable"
            v-model="searchText"
            placeholder="搜索..."
            clearable
            :prefix-icon="Search"
            @input="handleSearch"
            style="width: 200px"
          />
          <el-button
            v-if="refreshable"
            :icon="Refresh"
            circle
            @click="refreshData"
            :loading="loading"
          />
        </slot>
      </div>
    </div>

    <!-- 数据表格 -->
    <el-table
      ref="tableRef"
      v-loading="loading"
      :data="tableData"
      :height="height"
      :max-height="maxHeight"
      :stripe="stripe"
      :border="border"
      :row-key="rowKey"
      :highlight-current-row="highlightCurrentRow"
      :show-header="showHeader"
      :empty-text="emptyText"
      @sort-change="handleSortChange"
      @selection-change="handleSelectionChange"
      @row-click="handleRowClick"
    >
      <!-- 选择列 -->
      <el-table-column
        v-if="selectable"
        type="selection"
        width="50"
        align="center"
        fixed="left"
      />

      <!-- 序号列 -->
      <el-table-column
        v-if="showIndex"
        type="index"
        width="60"
        label="序号"
        align="center"
        :index="getIndex"
        fixed="left"
      />

      <!-- 动态列 -->
      <template v-for="column in columns" :key="column.prop">
        <el-table-column
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :min-width="column.minWidth"
          :fixed="column.fixed"
          :sortable="column.sortable"
          :align="column.align || 'left'"
          :show-overflow-tooltip="column.showOverflowTooltip !== false"
          :formatter="column.formatter"
        >
          <template #default="scope">
            <slot :name="column.prop" :row="scope.row" :column="column" :$index="scope.$index">
              <template v-if="column.type === 'tag'">
                <el-tag
                  :type="getTagType(scope.row, column)"
                  :effect="column.tagEffect || 'light'"
                  size="small"
                >
                  {{ scope.row[column.prop] }}
                </el-tag>
              </template>
              <template v-else-if="column.type === 'date'">
                {{ formatDate(scope.row[column.prop], column.dateFormat) }}
              </template>
              <template v-else-if="column.type === 'image'">
                <el-image
                  :src="scope.row[column.prop]"
                  :preview-src-list="[scope.row[column.prop]]"
                  fit="cover"
                  style="width: 40px; height: 40px"
                />
              </template>
              <template v-else-if="column.type === 'switch'">
                <el-switch
                  v-model="scope.row[column.prop]"
                  :disabled="column.disabled ? column.disabled(scope.row) : false"
                  @change="(val) => handleSwitchChange(val, scope.row, column)"
                />
              </template>
              <template v-else>
                {{ scope.row[column.prop] }}
              </template>
            </slot>
          </template>
        </el-table-column>
      </template>

      <!-- 操作列 -->
      <el-table-column
        v-if="showActions"
        :label="actionsLabel"
        :width="actionsWidth"
        :fixed="actionsFixed"
        align="center"
      >
        <template #default="scope">
          <slot name="actions" :row="scope.row" :$index="scope.$index">
            <el-button
              v-if="showEditButton"
              type="primary"
              size="small"
              :icon="Edit"
              circle
              @click.stop="handleEdit(scope.row)"
            />
            <el-button
              v-if="showDeleteButton"
              type="danger"
              size="small"
              :icon="Delete"
              circle
              @click.stop="handleDelete(scope.row)"
            />
          </slot>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页器 -->
    <div class="table-pagination" v-if="pagination && total > 0">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="pageSizes"
        :total="total"
        :layout="paginationLayout"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Search, Refresh, Edit, Delete } from '@element-plus/icons-vue';
import dayjs from 'dayjs';

const props = defineProps({
  // 数据源
  data: {
    type: Array,
    required: true
  },
  // 列定义
  columns: {
    type: Array,
    required: true
  },
  // 表格高度
  height: {
    type: [String, Number],
    default: null
  },
  // 表格最大高度
  maxHeight: {
    type: [String, Number],
    default: null
  },
  // 是否显示斑马纹
  stripe: {
    type: Boolean,
    default: true
  },
  // 是否显示边框
  border: {
    type: Boolean,
    default: true
  },
  // 行数据的键值
  rowKey: {
    type: String,
    default: 'id'
  },
  // 是否高亮当前行
  highlightCurrentRow: {
    type: Boolean,
    default: true
  },
  // 是否显示表头
  showHeader: {
    type: Boolean,
    default: true
  },
  // 空数据文本
  emptyText: {
    type: String,
    default: '暂无数据'
  },
  // 是否显示工具栏
  showToolbar: {
    type: Boolean,
    default: true
  },
  // 是否可搜索
  searchable: {
    type: Boolean,
    default: true
  },
  // 搜索字段
  searchFields: {
    type: Array,
    default: () => []
  },
  // 是否可刷新
  refreshable: {
    type: Boolean,
    default: true
  },
  // 是否可选择
  selectable: {
    type: Boolean,
    default: false
  },
  // 是否显示序号列
  showIndex: {
    type: Boolean,
    default: true
  },
  // 是否显示操作列
  showActions: {
    type: Boolean,
    default: true
  },
  // 操作列标题
  actionsLabel: {
    type: String,
    default: '操作'
  },
  // 操作列宽度
  actionsWidth: {
    type: [String, Number],
    default: 150
  },
  // 操作列是否固定
  actionsFixed: {
    type: String,
    default: 'right'
  },
  // 是否显示编辑按钮
  showEditButton: {
    type: Boolean,
    default: true
  },
  // 是否显示删除按钮
  showDeleteButton: {
    type: Boolean,
    default: true
  },
  // 是否启用分页
  pagination: {
    type: Boolean,
    default: true
  },
  // 总数据量
  total: {
    type: Number,
    default: 0
  },
  // 当前页码
  page: {
    type: Number,
    default: 1
  },
  // 每页数据量
  limit: {
    type: Number,
    default: 10
  },
  // 可选的每页数据量
  pageSizes: {
    type: Array,
    default: () => [10, 20, 50, 100]
  },
  // 分页布局
  paginationLayout: {
    type: String,
    default: 'total, sizes, prev, pager, next, jumper'
  },
  // 加载状态
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'update:page',
  'update:limit',
  'refresh',
  'search',
  'sort-change',
  'selection-change',
  'row-click',
  'edit',
  'delete',
  'switch-change'
]);

const tableRef = ref(null);
const searchText = ref('');
const currentPage = ref(props.page);
const pageSize = ref(props.limit);
const sortField = ref('');
const sortOrder = ref('');

// 计算表格数据
const tableData = computed(() => {
  let result = [...props.data];
  
  // 本地搜索
  if (searchText.value && !props.pagination) {
    const searchFields = props.searchFields.length > 0 
      ? props.searchFields 
      : props.columns.map(column => column.prop);
    
    result = result.filter(item => {
      return searchFields.some(field => {
        const value = item[field];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchText.value.toLowerCase());
      });
    });
  }
  
  // 本地排序
  if (sortField.value && !props.pagination) {
    result.sort((a, b) => {
      const valueA = a[sortField.value];
      const valueB = b[sortField.value];
      
      if (valueA === valueB) return 0;
      
      const result = valueA > valueB ? 1 : -1;
      return sortOrder.value === 'ascending' ? result : -result;
    });
  }
  
  // 本地分页
  if (!props.pagination) {
    const start = (currentPage.value - 1) * pageSize.value;
    const end = start + pageSize.value;
    result = result.slice(start, end);
  }
  
  return result;
});

// 监听页码变化
watch(() => props.page, (newVal) => {
  currentPage.value = newVal;
});

// 监听每页数量变化
watch(() => props.limit, (newVal) => {
  pageSize.value = newVal;
});

// 监听当前页码变化
watch(currentPage, (newVal) => {
  emit('update:page', newVal);
});

// 监听每页数量变化
watch(pageSize, (newVal) => {
  emit('update:limit', newVal);
});

// 处理页码变化
const handleCurrentChange = (val) => {
  currentPage.value = val;
};

// 处理每页数量变化
const handleSizeChange = (val) => {
  pageSize.value = val;
  currentPage.value = 1;
};

// 处理搜索
const handleSearch = () => {
  emit('search', searchText.value);
  if (!props.pagination) {
    currentPage.value = 1;
  }
};

// 处理排序
const handleSortChange = ({ prop, order }) => {
  sortField.value = prop;
  sortOrder.value = order;
  emit('sort-change', { prop, order });
};

// 处理选择变化
const handleSelectionChange = (selection) => {
  emit('selection-change', selection);
};

// 处理行点击
const handleRowClick = (row, column, event) => {
  emit('row-click', row, column, event);
};

// 处理编辑
const handleEdit = (row) => {
  emit('edit', row);
};

// 处理删除
const handleDelete = (row) => {
  emit('delete', row);
};

// 处理开关变化
const handleSwitchChange = (value, row, column) => {
  emit('switch-change', { value, row, column });
};

// 刷新数据
const refreshData = () => {
  emit('refresh');
};

// 获取序号
const getIndex = (index) => {
  return props.pagination 
    ? (currentPage.value - 1) * pageSize.value + index + 1 
    : index + 1;
};

// 格式化日期
const formatDate = (value, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!value) return '';
  return dayjs(value).format(format);
};

// 获取标签类型
const getTagType = (row, column) => {
  if (typeof column.tagType === 'function') {
    return column.tagType(row);
  }
  return column.tagType || '';
};

// 暴露方法
defineExpose({
  tableRef,
  refreshData,
  clearSelection: () => tableRef.value?.clearSelection(),
  toggleRowSelection: (row, selected) => tableRef.value?.toggleRowSelection(row, selected),
  toggleAllSelection: () => tableRef.value?.toggleAllSelection(),
  clearSort: () => tableRef.value?.clearSort(),
  clearFilter: () => tableRef.value?.clearFilter(),
  doLayout: () => tableRef.value?.doLayout(),
  sort: (prop, order) => tableRef.value?.sort(prop, order)
});
</script>

<style scoped>
.data-table {
  width: 100%;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-pagination {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style> 